// Global Operational Map — R2 Map Intelligence Engine
// SVG-based world intelligence visualization layer.
// Camera Controller: zoom, pan, fly-to city, location search, reset.
// Dark dotted world map with glowing, breathing teal/gold nodes.
// Live presence polling — privacy-safe segment + city only.
import { useEffect, useState, useRef, useCallback, type JSX } from 'react';
import { Maximize2, X, ZoomIn, ZoomOut, RotateCcw, Search, MapPin, Navigation } from 'lucide-react';
import { fetchActivity, countryFlag, formatUSD } from '../../lib/auction';

const MAP_SRC = `${import.meta.env.BASE_URL}ops-world-map.png`;

const BACKEND: string = (() => {
  const w = (typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>).__BACKEND_URL__
    : undefined) as string | undefined;
  return w || ((import.meta.env.VITE_BACKEND_URL as string) ?? '');
})();

type Segment = 'individual' | 'company' | 'enterprise';
interface LiveNode { lat: number; lng: number; city: string; country: string; flag: string; segment: Segment; }
interface Presence { nodes: LiveNode[]; counts: Record<Segment, number>; total: number; }

const SEG_STYLE: Record<Segment, { dot: string; ring: string; size: number; label: string }> = {
  enterprise: { dot: '#fcd34d', ring: 'rgba(245,158,11,0.55)', size: 13, label: 'Enterprise firm' },
  company:    { dot: '#38bdf8', ring: 'rgba(56,189,248,0.5)',  size: 10, label: 'Company' },
  individual: { dot: '#5eead4', ring: 'rgba(37,181,168,0.5)',  size: 7,  label: 'Individual' },
};

interface OpsNode { city: string; x: number; y: number; tone: 'teal' | 'gold'; }

// Hand-tuned positions (percent of map box) for a standard world layout.
const NODES: OpsNode[] = [
  { city: 'Calgary',   x: 20, y: 33, tone: 'gold' },
  { city: 'New York',  x: 26, y: 40, tone: 'teal' },
  { city: 'São Paulo', x: 33, y: 72, tone: 'teal' },
  { city: 'London',    x: 47, y: 33, tone: 'teal' },
  { city: 'Dubai',     x: 60, y: 46, tone: 'teal' },
  { city: 'Manila',    x: 80, y: 53, tone: 'gold' },
  { city: 'Singapore', x: 76, y: 60, tone: 'teal' },
  { city: 'Tokyo',     x: 85, y: 40, tone: 'teal' },
  { city: 'Sydney',    x: 87, y: 78, tone: 'teal' },
];

const TONE = {
  teal: { dot: '#5eead4', ring: 'rgba(37,181,168,0.55)' },
  gold: { dot: '#fcd34d', ring: 'rgba(245,158,11,0.55)' },
};

// Equirectangular projection → percentage position on the world-map image.
function projPct(lat: number, lng: number): { x: number; y: number } {
  return { x: ((lng + 180) / 360) * 100, y: ((90 - lat) / 180) * 100 };
}

interface CameraState { scale: number; tx: number; ty: number; }

interface MapBoxProps {
  full?: boolean;
  onExpand?: () => void;
  live?: LiveNode[];
  camera: CameraState;
  cameraAnim: boolean;
  onMapRef?: (el: HTMLDivElement | null) => void;
  searchCity?: string;
}

function MapBox({ full, onExpand, live, camera, cameraAnim, onMapRef, searchCity }: MapBoxProps): JSX.Element {
  const dot = full ? 10 : 7;
  const hasLive = !!live && live.length > 0;

  const highlightNode = searchCity
    ? NODES.find(n => n.city.toLowerCase().includes(searchCity.toLowerCase()))
    : null;

  return (
    <div
      onClick={!full ? onExpand : undefined}
      className={`relative w-full overflow-hidden border border-zinc-800/80 ${full ? 'rounded-xl h-full' : 'rounded-lg cursor-pointer group'}`}
      style={{
        height: full ? undefined : 232,
        backgroundColor: '#060b14',
      }}
      ref={onMapRef}
      data-testid={full ? 'ops-map-fullscreen-canvas' : 'ops-map-canvas'}
    >
      {/* Camera — everything inside zooms/pans */}
      <div
        style={{
          position: 'absolute', inset: 0,
          transform: `translate(${camera.tx}px, ${camera.ty}px) scale(${camera.scale})`,
          transformOrigin: '0 0',
          transition: cameraAnim ? 'transform 0.55s cubic-bezier(0.4,0,0.2,1)' : 'none',
        }}
      >
        {/* Map image inside camera so it zooms too */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${MAP_SRC})`,
          backgroundSize: '100% 100%',
        }} />

        {/* Real live users — classified by segment (anonymous) */}
        {hasLive && live!.map((n, i) => {
          const p = projPct(n.lat, n.lng);
          const s = SEG_STYLE[n.segment] ?? SEG_STYLE.individual;
          const size = full ? s.size + 2 : s.size;
          return (
            <div key={`live-${i}`} className="absolute" style={{ left: `${p.x}%`, top: `${p.y}%`, transform: 'translate(-50%,-50%)' }} data-testid={`live-node-${n.segment}`}>
              <span className="absolute inset-0 -m-2 rounded-full animate-ping" style={{ background: s.ring, animationDuration: '2.4s' }} />
              {n.segment === 'enterprise'
                ? <span className="block" style={{ width: size, height: size, background: s.dot, boxShadow: `0 0 10px ${s.dot}`, borderRadius: 3, border: '1px solid rgba(4,7,13,0.6)' }} />
                : <span className="block rounded-full" style={{ width: size, height: size, background: s.dot, boxShadow: `0 0 8px ${s.dot}` }} />}
              {full && <span className="absolute left-1/2 top-full mt-1 -translate-x-1/2 text-[10px] text-zinc-300 whitespace-nowrap">{n.flag} {n.city || n.country}</span>}
            </div>
          );
        })}

        {/* Simulated presence — shown when no live users yet */}
        {!hasLive && NODES.map((n) => (
          <div key={n.city} className="absolute" style={{ left: `${n.x}%`, top: `${n.y}%`, transform: 'translate(-50%,-50%)' }} data-testid={`ops-node-${n.city.toLowerCase().replace(/\s+/g, '-')}`}>
            <span className="absolute inset-0 -m-2 rounded-full animate-ping" style={{ background: TONE[n.tone].ring, animationDuration: '2.4s' }} />
            <span
              className="block rounded-full"
              style={{
                width: dot, height: dot,
                background: TONE[n.tone].dot,
                boxShadow: `0 0 ${highlightNode?.city === n.city ? '20px' : '8px'} ${TONE[n.tone].dot}`,
                outline: highlightNode?.city === n.city ? `2px solid ${TONE[n.tone].dot}` : 'none',
                outlineOffset: 3,
              }}
            />
            {full && <span className="absolute left-1/2 top-full mt-1 -translate-x-1/2 text-[10px] text-zinc-300 whitespace-nowrap">{n.city}</span>}
          </div>
        ))}
      </div>

      {/* Overlay gradient — outside camera, always full coverage */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, transparent 45%, rgba(6,11,20,0.65) 100%)' }} />

      {/* Legend */}
      <div className="absolute left-2 bottom-2 flex flex-col gap-1 rounded-md bg-black/50 border border-zinc-700/70 px-2 py-1.5" data-testid="ops-map-legend">
        {(['enterprise', 'company', 'individual'] as Segment[]).map((seg) => (
          <div key={seg} className="flex items-center gap-1.5 text-[9px] text-zinc-300">
            <span style={{ width: 8, height: 8, background: SEG_STYLE[seg].dot, boxShadow: `0 0 5px ${SEG_STYLE[seg].dot}`, borderRadius: seg === 'enterprise' ? 2 : '50%' }} />
            {SEG_STYLE[seg].label}
          </div>
        ))}
      </div>

      {!full && (
        <div className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-md bg-black/40 border border-zinc-700 text-zinc-300 opacity-70 group-hover:opacity-100 group-hover:text-amber-300 group-hover:border-amber-300/50 transition" data-testid="ops-map-expand-hint">
          <Maximize2 className="h-3.5 w-3.5" />
        </div>
      )}
    </div>
  );
}

// ── Camera Controls Bar ────────────────────────────────────────────────────────
function CameraControls({
  onZoomIn, onZoomOut, onReset, onFlyTo, nodes, searchVal, onSearch,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  onFlyTo: (cx: number, cy: number, scale?: number) => void;
  nodes: OpsNode[];
  searchVal: string;
  onSearch: (v: string) => void;
}): JSX.Element {
  const btnStyle = (accent?: string): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 7, cursor: 'pointer',
    background: 'rgba(6,10,22,0.85)',
    border: `1px solid ${accent ?? 'rgba(255,255,255,0.1)'}`,
    color: accent ?? 'rgba(148,172,210,0.75)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.15s',
    fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' as const,
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onSearch(v);
    if (!v) return;
    const match = nodes.find(n => n.city.toLowerCase().startsWith(v.toLowerCase()));
    if (match) onFlyTo(match.x, match.y, 3);
  };

  return (
    <div
      data-testid="map-camera-controls"
      style={{
        position: 'absolute', top: 8, right: 8,
        display: 'flex', flexDirection: 'column', gap: 5, zIndex: 20,
      }}
    >
      {/* Location search */}
      <div style={{ position: 'relative' }}>
        <input
          data-testid="map-search-input"
          placeholder="Search city…"
          value={searchVal}
          onChange={handleSearch}
          style={{
            width: 130, padding: '5px 8px 5px 24px', borderRadius: 7, fontSize: 10,
            background: 'rgba(6,10,22,0.92)', border: '1px solid rgba(255,255,255,0.1)',
            color: '#e2ecfa', outline: 'none', backdropFilter: 'blur(10px)',
          }}
        />
        <Search size={11} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: 'rgba(94,234,212,0.5)', pointerEvents: 'none' }} />
      </div>

      {/* Zoom controls */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <button data-testid="map-zoom-in"  style={btnStyle()} onClick={onZoomIn}  title="Zoom in"><ZoomIn  size={13} /></button>
        <button data-testid="map-zoom-out" style={btnStyle()} onClick={onZoomOut} title="Zoom out"><ZoomOut size={13} /></button>
        <button data-testid="map-reset"    style={btnStyle()} onClick={onReset}   title="Reset view"><RotateCcw size={12} /></button>
      </div>

      {/* Quick-fly buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
        <button
          data-testid="map-fly-calgary"
          onClick={() => { onSearch('Calgary'); onFlyTo(20, 33, 3.5); }}
          style={{ ...btnStyle('rgba(245,158,11,0.6)'), width: 'auto', padding: '4px 8px', gap: 4 }}
          title="Zoom to Calgary — R2 HQ"
        >
          <MapPin size={10} style={{ color: '#fcd34d' }} />
          <span style={{ color: '#fcd34d', fontSize: 9 }}>Calgary HQ</span>
        </button>
        <button
          data-testid="map-fly-world"
          onClick={() => { onSearch(''); onReset(); }}
          style={{ ...btnStyle(), width: 'auto', padding: '4px 8px', gap: 4 }}
          title="World view"
        >
          <Navigation size={10} />
          <span style={{ fontSize: 9 }}>World</span>
        </button>
      </div>
    </div>
  );
}

export function GlobalOperationalMap({ standalone = false }: { standalone?: boolean } = {}): JSX.Element {
  const [tick, setTick] = useState(0);
  const [bidFeed, setBidFeed] = useState<string[]>([]);
  const [full, setFull] = useState(false);
  const [presence, setPresence] = useState<Presence | null>(null);

  // Camera state
  const [camera, setCamera]     = useState<CameraState>({ scale: 1, tx: 0, ty: 0 });
  const [cameraAnim, setCameraAnim] = useState(false);
  const [searchVal, setSearchVal]   = useState('');
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  const flyTo = useCallback((cx: number, cy: number, scale = 3) => {
    const el = mapContainerRef.current;
    if (!el) return;
    const W = el.clientWidth;
    const H = el.clientHeight;
    setCameraAnim(true);
    setCamera({
      scale,
      tx: W * (0.5 - (cx / 100) * scale),
      ty: H * (0.5 - (cy / 100) * scale),
    });
  }, []);

  const resetView = useCallback(() => {
    setCameraAnim(true);
    setCamera({ scale: 1, tx: 0, ty: 0 });
    setSearchVal('');
  }, []);

  const zoomStep = useCallback((dir: 1 | -1) => {
    setCameraAnim(true);
    setCamera(prev => {
      const s = Math.max(1, Math.min(5, prev.scale * (dir > 0 ? 1.35 : 1 / 1.35)));
      if (s <= 1.05) return { scale: 1, tx: 0, ty: 0 };
      return { ...prev, scale: s };
    });
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async (): Promise<void> => {
      try {
        const r = await fetch(`${BACKEND}/api/presence/live`, { signal: AbortSignal.timeout(6000) });
        if (!alive || !r.ok) return;
        setPresence(await r.json() as Presence);
      } catch { /* keep last */ }
    };
    void load();
    const t = setInterval(() => void load(), 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async (): Promise<void> => {
      try {
        const events = await fetchActivity(8);
        if (!alive) return;
        setBidFeed(events.map((e) => `New Bid · ${countryFlag(e.country)} ${e.country} · ${formatUSD(e.bid_amount)}`));
      } catch { /* keep last */ }
    };
    void load();
    const t = setInterval(() => void load(), 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const items = bidFeed;

  useEffect(() => {
    if (items.length === 0) { setTick(0); return; }
    const t = setInterval(() => setTick((n) => (n + 1) % items.length), 2600);
    return () => clearInterval(t);
  }, [items.length]);

  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setFull(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);

  const feed = items.length > 0 ? [0, 1, 2].map((i) => items[(tick + i) % items.length] ?? '') : [];

  const Ticker = (
    <div className="mt-3 rounded-lg border border-zinc-800/70 bg-zinc-900/40 px-3 py-2" data-testid="ops-ticker">
      {feed.length === 0 ? (
        <div className="flex items-center gap-2 text-[11px]" style={{ height: 18 }} data-testid="ops-ticker-empty">
          <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-zinc-600" />
          <span className="text-zinc-500 truncate font-mono">Awaiting live activity…</span>
        </div>
      ) : feed.map((m, i) => (
        <div key={m + i} className="flex items-center gap-2 text-[11px] transition-opacity" style={{ opacity: i === 0 ? 1 : 0.45 - i * 0.12, height: 18 }}>
          <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: i === 0 ? '#22c55e' : '#3f6212' }} />
          <span className="text-zinc-400 truncate font-mono">{m}</span>
          {i === 0 && <span className="text-emerald-400 text-[10px] ml-auto shrink-0">live</span>}
        </div>
      ))}
    </div>
  );

  const CountsLine = presence && presence.total > 0 ? (
    <span className="text-[10px] flex items-center gap-2" data-testid="presence-counts">
      <span style={{ color: '#fcd34d' }}>{presence.counts.enterprise} Enterprise</span>
      <span style={{ color: '#38bdf8' }}>{presence.counts.company} Company</span>
      <span style={{ color: '#5eead4' }}>{presence.counts.individual} Individual</span>
    </span>
  ) : null;

  const commonMapProps = {
    camera,
    cameraAnim,
    live: presence?.nodes,
    searchCity: searchVal,
  };

  if (standalone) {
    return (
      <div data-testid="global-ops-map" className="flex flex-col h-full min-h-0">
        {CountsLine && (
          <div className="flex items-center gap-2 mb-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Live users</span>
            {CountsLine}
          </div>
        )}
        <div className="flex-1 min-h-0 relative">
          <MapBox full onMapRef={(el) => { mapContainerRef.current = el; }} {...commonMapProps} />
        </div>
        {Ticker}
      </div>
    );
  }

  return (
    <div data-testid="global-ops-map" className="w-full">
      <div className="flex items-center gap-2 mb-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Global Operations · Live</span>
        {CountsLine && <span className="ml-2">{CountsLine}</span>}
        <span className="text-[9px] text-zinc-600 ml-auto">click to expand</span>
      </div>

      <MapBox onExpand={() => setFull(true)} {...commonMapProps} onMapRef={() => {}} />
      {Ticker}

      {/* One-click fullscreen overlay */}
      {full && (
        <div className="fixed inset-0 z-[80] bg-[#04070d]/95 backdrop-blur-sm flex flex-col p-4 md:p-8" data-testid="ops-map-fullscreen" onClick={() => setFull(false)}>
          <div className="flex items-center justify-between mb-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm font-semibold text-white tracking-wide">RCD² Global Operations — Live Network</span>
              <span className="text-[9px] text-zinc-600 ml-2">right-click map to zoom · Calgary = R2 HQ</span>
            </div>
            <button data-testid="ops-map-close" onClick={() => setFull(false)} className="h-9 w-9 grid place-items-center rounded-lg border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 transition">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 min-h-0 relative" onClick={(e) => e.stopPropagation()}>
            <MapBox
              full
              onMapRef={(el) => { mapContainerRef.current = el; }}
              {...commonMapProps}
            />
            {/* Camera controls — only in fullscreen */}
            <CameraControls
              onZoomIn={() => zoomStep(1)}
              onZoomOut={() => zoomStep(-1)}
              onReset={resetView}
              onFlyTo={flyTo}
              nodes={NODES}
              searchVal={searchVal}
              onSearch={setSearchVal}
            />
          </div>

          <div onClick={(e) => e.stopPropagation()}>{Ticker}</div>
        </div>
      )}
    </div>
  );
}
